import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box, Stack, Typography, IconButton } from '@mui/material';
import Iconify from '@/components/Iconify';
import useMobile from '@/hooks/useMobile';
import { fCurrency } from '@/utils/formatNumber';
import { formatLocalDate } from '@/locales/dates';
import useEventContext from '@/contexts/event/useEventContext';
import { shouldEventHideBines } from '@/config/hideBines';

export default function MsiDetails({
  data,
  title,
  detailTitle,
  onClose = () => {},
  eventId,
}) {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { minimumPurchase, binesByBanks } = data;

  // No se realiza una destructuración porque el componente se usa en el home
  // donde se tiene acceso al contexto y se usa en la pantalla de pago donde
  // no setiene acceso al contexto. Para ello es que se pasa la prop eventId
  const eventData = useEventContext();
  const id = eventData?.event?.event?.id;

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : '522px',
        mb: 4,
      }}
    >
      <Container>
        <Stack
          direction="row"
          sx={{
            width: '100%',
            height: '56px',
            alignItems: 'center',
            mb: 2,
            mt: 2,
          }}
        >
          <Box>
            <Typography variant="body4" component={'div'}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          <IconButton onClick={onClose}>
            <Iconify width={20} height={20} icon={'mdi:close'} />
          </IconButton>
        </Stack>
        <Box>
          <Typography
            variant="body2"
            component={'div'}
            sx={{ fontWeight: 600 }}
          >
            {`${t('checkout.global.dates')}:`}
          </Typography>
          <Typography variant="caption2" component={'div'}>
            {`${t('checkout.global.start')}: ${formatLocalDate(data.startAt, {
              time: 'hh:mm A',
            })}`}
          </Typography>
          <Typography variant="caption2" component={'div'}>
            {`${t('checkout.global.end')}: ${formatLocalDate(data.endAt, {
              time: 'hh:mm A',
            })}`}
          </Typography>
        </Box>
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="body2"
            component={'div'}
            sx={{ fontWeight: 600 }}
          >
            {`${t('checkout.msi.participatingCards')}:`}
          </Typography>

          {Object.keys(binesByBanks).map((bank) => (
            <Box key={bank} sx={{ mt: 2 }}>
              <Typography variant="caption2" component={'div'}>
                {t('checkout.msi.payWith', { bank })}
              </Typography>
              <Box
                sx={{
                  paddingLeft: '26px',
                }}
              >
                <ul>
                  {binesByBanks[bank]
                    .filter((card) => card)
                    .filter((card, index, self) => {
                      return shouldEventHideBines(eventId || id)
                        ? index ===
                            self.findIndex(
                              (c) =>
                                c.card_name.trim() === card.card_name.trim(),
                            )
                        : card;
                    })
                    .map((card) => (
                      <li key={card.card_name}>
                        <Typography variant="caption2" component={'div'}>
                          {`${card.card_name}`}{' '}
                          {shouldEventHideBines(eventId || id)
                            ? ''
                            : `(${card.bin})`}{' '}
                          {card.msi ? 'Aplica MSI' : ''}
                        </Typography>
                      </li>
                    ))}
                </ul>
              </Box>
              {shouldEventHideBines(eventId || id) && (
                <Box mt={2}>
                  <Typography variant="caption">
                    * No aplican tarjetas empresariales
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
        {minimumPurchase && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="body2"
              component={'div'}
              sx={{ fontWeight: 600 }}
            >
              {t('checkout.msi.min')}
            </Typography>
            <Typography variant="caption2" component={'div'}>
              {t('checkout.msi.toValidPromo', {
                minimumPurchase: fCurrency(minimumPurchase),
              })}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
